import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type JSX,
} from 'react';
import { mutate as globalMutate } from 'swr';
import { useAtom } from 'ximple';
import { NativeMember, NativeMemberLoginResponse, NativeMemberRegisterRequest } from '@repo/types';
import { useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import {
    nativeMemberLogin,
    nativeMemberLogout,
    nativeMemberRegister,
} from '@repo/widget-utils/services/fetchers/native-membership';
import {
    nativeMemberMeKey,
    useNativeMember,
} from '@repo/widget-utils/services/hooks/native-membership';
import { ApiError } from '@repo/widget-utils/services/utils/ApiError';
import { nativeMemberTokenAtom } from 'src/state/native-membership/nativeMemberTokenAtom';
import NativeLoginModal from './NativeLoginModal';

export type AuthMode = 'login' | 'signup';

type NativeMemberContextState = {
    isNative: boolean;
    token: string | null;
    member: NativeMember | null;
    isLoadingMember: boolean;
    isLoggedIn: boolean;
    isLoginModalOpen: boolean;
    mode: AuthMode;
    setMode: (mode: AuthMode) => void;
    openAuthModal: (mode?: AuthMode) => void;
    openLoginModal: () => void;
    closeLoginModal: () => void;
    login: (email: string, password: string) => Promise<void>;
    register: (payload: NativeMemberRegisterRequest) => Promise<void>;
    logout: () => Promise<void>;
};

const NativeMemberContext = createContext<NativeMemberContextState | null>(null);

// Always mounted (mount-both-inert): when this tenant is not native, the provider
// fetches nothing and the login modal is not rendered. Legacy/guest behavior is
// unaffected. Nested inside the legacy auth/member providers in WidgetWrapper.
export function NativeMemberContextProvider(props: PropsWithChildren): JSX.Element {
    const isNative = useMembershipMode() === 'native';
    const [token] = useAtom(nativeMemberTokenAtom);
    const [isLoginModalOpen, setLoginModalOpen] = useState(false);
    const [mode, setMode] = useState<AuthMode>('login');

    const {
        data: member,
        error: memberError,
        isLoading: isLoadingMember,
    } = useNativeMember(isNative ? token : null);

    const openAuthModal = useCallback((nextMode: AuthMode = 'login') => {
        setMode(nextMode);
        setLoginModalOpen(true);
    }, []);
    const openLoginModal = useCallback(() => openAuthModal('login'), [openAuthModal]);
    const closeLoginModal = useCallback(() => setLoginModalOpen(false), []);

    // Login and register end the same way: store the PAT, seed the profile cache so it is
    // available immediately (no refetch flash), and close the modal.
    const applyAuthenticated = useCallback(async (response: NativeMemberLoginResponse) => {
        await nativeMemberTokenAtom.update(response.token);
        await globalMutate(nativeMemberMeKey(response.token), response.member, {
            revalidate: false,
        });
        setLoginModalOpen(false);
    }, []);

    const login = useCallback(
        async (email: string, password: string) => {
            await applyAuthenticated(await nativeMemberLogin(email, password));
        },
        [applyAuthenticated],
    );

    const register = useCallback(
        async (payload: NativeMemberRegisterRequest) => {
            await applyAuthenticated(await nativeMemberRegister(payload));
        },
        [applyAuthenticated],
    );

    const logout = useCallback(async () => {
        const currentToken = token;
        await nativeMemberTokenAtom.update(null);
        if (currentToken) {
            try {
                await nativeMemberLogout(currentToken);
            } catch {
                // Best-effort server-side revoke; the local token is already cleared.
            }
        }
    }, [token]);

    // Expired/invalid token: drop it and prompt re-auth. There is no refresh token
    // by design, so a 401 from the profile call means the member must sign in again.
    useEffect(() => {
        if (isNative && token && memberError instanceof ApiError && memberError.status === 401) {
            void nativeMemberTokenAtom.update(null);
            openAuthModal('login');
        }
    }, [isNative, token, memberError, openAuthModal]);

    const value = useMemo<NativeMemberContextState>(
        () => ({
            isNative,
            token,
            member: member ?? null,
            isLoadingMember,
            isLoggedIn: Boolean(token && member),
            isLoginModalOpen,
            mode,
            setMode,
            openAuthModal,
            openLoginModal,
            closeLoginModal,
            login,
            register,
            logout,
        }),
        [
            isNative,
            token,
            member,
            isLoadingMember,
            isLoginModalOpen,
            mode,
            openAuthModal,
            openLoginModal,
            closeLoginModal,
            login,
            register,
            logout,
        ],
    );

    return (
        <NativeMemberContext.Provider value={value}>
            {props.children}
            {isNative && <NativeLoginModal />}
        </NativeMemberContext.Provider>
    );
}

export function useNativeMemberContext(): NativeMemberContextState {
    const ctx = useContext(NativeMemberContext);
    if (!ctx) {
        throw new Error('useNativeMemberContext must be used within a NativeMemberContextProvider');
    }
    return ctx;
}
